import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Tab, Tabs,
  TextField, InputAdornment,
} from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SearchIcon from "@mui/icons-material/Search";
import { axiosPartners } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const STATUS_TABS = ["OPEN", "COMPLETED", "CANCELLED", "INACTIVE"];

const statusChipColor = (status) => {
  const s = status?.toUpperCase() || "";
  if (s === "COMPLETED")   return { bg: "#dcfce7", color: "#16a34a" };
  if (s === "OPEN")        return { bg: "#dbeafe", color: "#2563eb" };
  if (s === "CANCELLED")   return { bg: "#fee2e2", color: "#dc2626" };
  if (s === "INACTIVE")    return { bg: "#f1f5f9", color: "#64748b" };
  if (s === "CLOSED")      return { bg: "#dcfce7", color: "#16a34a" };
  if (s.includes("GREEN")) return { bg: "#dcfce7", color: "#16a34a" };
  if (s.includes("RED"))   return { bg: "#fee2e2", color: "#dc2626" };
  if (s === "PENDING")     return { bg: "#fef9c3", color: "#b45309" };
  return { bg: "#f1f5f9", color: "#64748b" };
};

const SymbolRow = ({ symbolName, trades, index, onSymbolClick }) => {
  const buyCount  = trades.filter((t) => t.entry_signal?.toLowerCase() === "buy").length;
  const sellCount = trades.length - buyCount;

  const brokers = [...new Set(trades.map((t) => t.broker_name).filter(Boolean))];

  const avgGreen = trades.reduce((s, t) => s + (t.executed_green_price ?? t.green_price ?? 0), 0) / trades.length;
  const avgRed   = trades.reduce((s, t) => s + (t.executed_red_price ?? t.red_alert_price ?? 0), 0) / trades.length;

  const orderStatusCounts = trades.reduce((acc, t) => {
    const s = t.order_status || "UNKNOWN";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const exitReasons = [...new Set(trades.map((t) => t.exit_reason).filter(Boolean))];

  const tradingSymbol = trades[0]?.trading_symbol || "";
  const exchange      = trades[0]?.exchange_segment || "";
  const dayHigh       = trades[0]?.day_high;
  const dayLow        = trades[0]?.day_low;

  return (
    <TableRow
      onClick={() => onSymbolClick(symbolName, trades)}
      sx={{ cursor: "pointer", "&:hover": { background: "#f0fdf4" }, transition: "background 0.15s" }}
    >
      <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5 }}>{index + 1}</TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#04343a", lineHeight: 1.3 }}>{symbolName}</Typography>
        {(tradingSymbol || exchange) && (
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{tradingSymbol}{exchange ? ` · ${exchange}` : ""}</Typography>
        )}
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>{trades.length}</Typography>
        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>trades</Typography>
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
          <Chip label={`Buy ${buyCount}`}  size="small" sx={{ background: "#dcfce7", color: "#16a34a", fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
          <Chip label={`Sell ${sellCount}`} size="small" sx={{ background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
        </Box>
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {brokers.map((b) => (
            <Chip key={b} label={b} size="small" sx={{ background: "#eff6ff", color: "#2563eb", fontWeight: 600, fontSize: 11, height: 22, borderRadius: "6px" }} />
          ))}
        </Box>
      </TableCell>

      <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
        <Typography sx={{ fontSize: 12 }}>
          <span style={{ color: "#16a34a", fontWeight: 700 }}>G </span>
          {avgGreen ? avgGreen.toFixed(2) : "—"}
        </Typography>
        <Typography sx={{ fontSize: 12 }}>
          <span style={{ color: "#dc2626", fontWeight: 700 }}>R </span>
          {avgRed ? avgRed.toFixed(2) : "—"}
        </Typography>
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
          {Object.entries(orderStatusCounts).map(([s, cnt]) => {
            const c = statusChipColor(s);
            return (
              <Chip key={s} label={`${s} (${cnt})`} size="small"
                sx={{ background: c.bg, color: c.color, fontWeight: 700, fontSize: 10, height: 20, borderRadius: "4px" }} />
            );
          })}
        </Box>
        {exitReasons.length > 0 && (
          <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>{exitReasons.join(", ")}</Typography>
        )}
      </TableCell>

      <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
        <Typography sx={{ fontSize: 11 }}><span style={{ color: "#16a34a", fontWeight: 600 }}>H</span> {dayHigh ?? "—"}</Typography>
        <Typography sx={{ fontSize: 11 }}><span style={{ color: "#dc2626", fontWeight: 600 }}>L</span> {dayLow ?? "—"}</Typography>
      </TableCell>

    </TableRow>
  );
};

const TradeDashboardSymbolwiseDrilldown = () => {
  const [rawData, setRawData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("OPEN");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const fetchData = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await axiosPartners.get(`/brokers/open-trade-dashboard-symbolwise/?status=${status}`);
      setRawData(res.data?.response ?? {});
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(activeTab); }, [fetchData, activeTab]);

  const symbolGroups = useMemo(() =>
    Object.entries(rawData)
      .map(([symbolName, val]) => ({
        symbolName,
        trades: val?.data || [],
      }))
      .filter((g) => g.trades.length > 0)
      .sort((a, b) => b.trades.length - a.trades.length),
  [rawData]);

  const filtered = useMemo(() =>
    symbolGroups.filter((g) =>
      g.symbolName.toLowerCase().includes(search.toLowerCase())
    ),
  [symbolGroups, search]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setSearch("");
  };

  const handleSymbolClick = (symbolName, trades) => {
    const symbolId = trades[0]?.symbol;
    navigate(`../symbolwise/${encodeURIComponent(symbolName)}/brokers`, {
      relative: "path",
      state: { symbolId, symbolName },
    });
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", px: 3, py: 2, mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 4px 20px rgba(4,52,58,0.3)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ShowChartIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>Trade Dashboard — Symbolwise</Typography>
            <Typography sx={{ color: "#b8d4c4", fontSize: 12 }}>Click a symbol to see broker breakdown</Typography>
          </Box>
        </Box>
        <TextField
          size="small"
          placeholder="Search symbol…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "#b8d4c4" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 220,
            "& .MuiOutlinedInput-root": {
              background: "rgba(255,255,255,0.1)",
              borderRadius: "8px",
              color: "#fff",
              fontSize: 13,
              "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
              "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
              "&.Mui-focused fieldset": { borderColor: "#b8d4c4" },
            },
            "& input::placeholder": { color: "#b8d4c4", opacity: 1 },
          }}
        />
      </Box>

      {/* Status Tabs */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", mb: 2, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 1,
            "& .MuiTabs-indicator": { backgroundColor: "#016551", height: 3, borderRadius: "2px 2px 0 0" },
            "& .MuiTab-root": { fontWeight: 600, fontSize: 13, textTransform: "none", color: "#64748b", minHeight: 48, px: 3 },
            "& .Mui-selected": { color: "#016551 !important" },
          }}
        >
          {STATUS_TABS.map((tab) => (
            <Tab key={tab} label={tab} value={tab} />
          ))}
        </Tabs>
      </Box>

      {/* Table */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ background: "#04343a", px: 3, py: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
            {filtered.length} Symbol{filtered.length !== 1 ? "s" : ""}
            {search && <span style={{ color: "#b8d4c4", fontWeight: 400, fontSize: 12, marginLeft: 8 }}>matching "{search}"</span>}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#016551" }} size={36} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No trades found for {activeTab}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Symbol", "Count", "Buy / Sell", "Brokers", "Green / Red", "Order Status", "Day H/L"].map((h) => (
                    <TableCell key={h} sx={{ background: "#04343a", color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", py: 1.5, whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(({ symbolName, trades }, i) => (
                  <SymbolRow
                    key={symbolName}
                    index={i}
                    symbolName={symbolName}
                    trades={trades}
                    onSymbolClick={handleSymbolClick}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default TradeDashboardSymbolwiseDrilldown;
